import { ecartJours } from './calendrier'
import { montantDansTexte, periodeDansTexte } from './collecte-locale'
import { adresseDe, expediteurCorrespond } from './consignes'
import type { ClassementMailCode, TypePiece } from './domaine'
import { memeCloison } from './espaces'
import { contientNumero, normaliserLibelle } from './libelle'
import { dernierJourDePeriode } from './periode'
import { sansAccents } from './texte'

// Pièce annoncée par mail, arrivée par un autre canal : un mail dit « votre facture est
// disponible » sans la joindre (tâche « récupérer la pièce », ou « vérifier un mail » quand
// l'entité ou le classement est douteux), et le document arrive par le
// portail, un dépôt, une photo ou une plateforme, avant ou après le mail, quel que soit le
// fournisseur. Sans modèle : l'émetteur de la pièce correspond à l'expéditeur du mail (adresse
// connue du tiers, ou un mot distinctif commun), sa date précède de peu le mail, et ce que le
// mail dit (nature, montant, numéro, mois) ne la contredit pas. Un mail qui ne nomme pas la
// nature du document vient souvent d'une plateforme (banque, logiciel, signature) plutôt que de
// son émetteur : il faut alors le numéro ou le montant. Une pièce rejoint une tâche au plus,
// une tâche reçoit une pièce au plus : la plus proche, puis la plus ancienne tâche. Jamais à
// travers la cloison d'un espace privé (une tâche sans entité est dans la cloison commune).

/** Tâches nées d'un mail qui peuvent attendre une pièce annoncée. */
export const ACTIONS_PIECE_ANNONCEE = ['recuperer_piece', 'verifier_mail'] as const

/**
 * La tâche d'un mail attend-elle la pièce qu'il annonce ? « Récupérer la pièce », toujours ;
 * « vérifier un mail », quand tous ses mails annoncent une pièce (`facture_ou_piece`) : le doute
 * (entité non reconnue, classement peu sûr) n'empêche pas la pièce arrivée par un autre canal
 * d'y répondre, la règle exigeant de toute façon l'émetteur et la nature. Jamais une demande,
 * un mail illisible (classement nul) ni une autre tâche.
 */
export function attendPieceAnnoncee(
  action: string | null,
  classements: readonly (ClassementMailCode | null)[],
): boolean {
  if (action === 'recuperer_piece') return true
  return (
    action === 'verifier_mail' &&
    classements.length > 0 &&
    classements.every((c) => c === 'facture_ou_piece')
  )
}

/**
 * Une pièce peut-elle terminer la tâche ? Sans échéance, oui. Celle d'une échéance couvre toute
 * sa période (« les factures Uber pro du mois ») : un mail que la consigne y a joint annonce une
 * de ces pièces, pas toutes ; la tâche attend donc la fin de la période (`periode`, canonique),
 * jusqu'à son dernier jour compris ; de même, elle n'appelle son collecteur qu'ensuite (page
 * « Collecteurs »). `jour` : aujourd'hui (AAAA-MM-JJ, Paris).
 */
export function attendUnePiece(periode: string | null, jour: string): boolean {
  const fin = periode ? dernierJourDePeriode(periode) : null
  return fin === null || jour > fin
}

export type RecuperationAttendue = {
  tacheId: string
  /** Null : tâche sans entité (administrateurs), toute pièce commune peut y répondre. */
  entiteId: string | null
  /** En-tête « Nom <adresse> » du mail. */
  expediteur: string | null
  /** Objet et résumé du mail : ce qu'il dit du document (montant, numéro, mois). */
  objet: string | null
  resume: string | null
  /** Jour de réception du mail (AAAA-MM-JJ, Paris). */
  jour: string
}

export type PieceArrivee = {
  pieceId: string
  entiteId: string
  type: TypePiece
  /** Noms de l'émetteur : le tiers rattaché, le nom lu sur la pièce, le fournisseur du portail. */
  noms: readonly string[]
  /** Adresses ou domaines d'où le tiers émetteur écrit. */
  adresses: readonly string[]
  numero: string | null
  /** Date du document, sinon jour d'arrivée (AAAA-MM-JJ). */
  jour: string
  /** Montants lus (TTC, à payer), en centimes. */
  montantsCts: readonly number[]
}

export type SignalRattachement = 'adresse' | 'nom' | 'nature' | 'numero' | 'montant' | 'mois'

export type CorrespondanceRecuperation = {
  tacheId: string
  pieceId: string
  score: number
  /** Ce qui a fait reconnaître la pièce, dans cet ordre. */
  signaux: SignalRattachement[]
}

/** Un document est annoncé après son émission : jusqu'à 45 jours avant le mail… */
export const AVANT_MAIL_JOURS = 45
/** … ou relevé sur le portail juste après lui. */
export const APRES_MAIL_JOURS = 5

const POIDS: Record<SignalRattachement, number> = {
  adresse: 50,
  nom: 40,
  nature: 10,
  numero: 30,
  montant: 25,
  mois: 10,
}
/** Au plus pour la date : décroît avec l'écart au mail. */
const POIDS_DATE = 20

/**
 * Mots qui ne désignent pas un fournisseur : boîte d'envoi, formules, formes sociales, métiers
 * et pays. Ils ne départagent rien (« Service client », « Orange Telecom »). En minuscules et
 * sans accents ; partagés avec le rapprochement des justificatifs.
 */
export const MOTS_GENERIQUES: ReadonlySet<string> = new Set(
  (
    'espace client clients clientele service services noreply reply repondre info infos ' +
    'information informations contact facture factures facturation billing invoice invoices ' +
    'payment payments paiement paiements notification notifications mail email mails www ' +
    'com net org sas sarl sasu eurl ltd limited inc gmbh the des les and pour groupe group ' +
    'compte comptes mon votre vos your team equipe support account accounts receipts receipt ' +
    'recu recus document documents conseiller conseillere particulier particuliers pro ' +
    'professionnel professionnels entreprise entreprises news newsletter alerte alertes ' +
    'telecom telecoms energie energies assurance assurances mutuelle banque bank eau eaux ' +
    'mobile internet electricite gaz distribution france ireland irlande luxembourg europe ' +
    'international'
  ).split(' '),
)

/** Mot qui ne désigne pas un fournisseur (« Énergie », « Télécom ») : il ne départage rien. */
export function motGenerique(mot: string): boolean {
  return MOTS_GENERIQUES.has(sansAccents(mot))
}

/** Tous les mots d'un texte, sans accents ni casse (génériques compris). */
const tousLesMots = (texte: string) =>
  sansAccents(texte)
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length > 0)

/** Mots qui peuvent désigner un fournisseur : trois signes au moins, ni génériques ni nombres. */
const motsDistinctifs = (mots: readonly string[]) => [
  ...new Set(mots.filter((m) => m.length >= 3 && !MOTS_GENERIQUES.has(m) && !/^\d+$/.test(m))),
]

/** Mots de l'expéditeur : nom affiché et domaine sans son extension ; jamais le nom de la boîte. */
function motsBrutsExpediteur(de: string | null): string[] {
  if (!de) return []
  const adresse = adresseDe(de)
  const affiche = de.replace(/<[^>]*>/g, ' ').replace(/[^\s<>"]+@[^\s<>"]+/g, ' ')
  const domaine = adresse
    ? adresse
        .slice(adresse.lastIndexOf('@') + 1)
        .split('.')
        .slice(0, -1)
        .join(' ')
    : ''
  return [...tousLesMots(affiche), ...tousLesMots(domaine)]
}

export function motsExpediteur(de: string | null): string[] {
  return motsDistinctifs(motsBrutsExpediteur(de))
}

/**
 * Deux mots désignent le même fournisseur : égaux, ou l'un contient l'autre (domaine en un mot,
 * « bouyguestelecom ») quand le plus court est long, ou que le reste est un mot de l'autre côté.
 * Mots en minuscules et sans accents ; `autres` : tous les mots des deux côtés, génériques
 * compris. Partagé avec le rapprochement des justificatifs.
 */
export function motsProches(a: string, b: string, autres: ReadonlySet<string>): boolean {
  if (a === b) return true
  const [court, long] = a.length <= b.length ? [a, b] : [b, a]
  if (court.length < 4) return false
  if (long.startsWith(court)) return court.length >= 6 || autres.has(long.slice(court.length))
  if (long.endsWith(court))
    return court.length >= 6 || autres.has(long.slice(0, long.length - court.length))
  return court.length >= 6 && long.includes(court)
}

/** L'expéditeur du mail et l'un des noms de l'émetteur ont un mot distinctif commun. */
export function memeEmetteur(de: string | null, noms: readonly string[]): boolean {
  const brutsMail = motsBrutsExpediteur(de)
  const brutsPiece = noms.flatMap(tousLesMots)
  const mail = motsDistinctifs(brutsMail)
  const piece = motsDistinctifs(brutsPiece)
  const tous = new Set([...brutsMail, ...brutsPiece])
  return mail.some((x) => piece.some((y) => motsProches(x, y, tous)))
}

/** Natures de document que le mail nomme (« votre facture », « billing », « avis de prélèvement »). */
const NATURES: readonly [RegExp, TypePiece][] = [
  [/\b(factur\w*|invoices?|billing)\b/, 'facture'],
  [/\bavis\b/, 'avis'],
  [/\b(releves?|statements?)\b/, 'releve'],
  [/\b(bulletins? de (paie|salaire)|fiches? de paie)\b/, 'fiche_paie'],
  [/\baccuses? de (depot|reception)\b/, 'accuse_depot'],
  [/\bcontrats?\b/, 'contrat'],
]

export function naturesAnnoncees(texte: string): Set<TypePiece> {
  const t = sansAccents(texte)
  return new Set(NATURES.filter(([motif]) => motif.test(t)).map(([, nature]) => nature))
}

/**
 * Reçu d'un achat (« Reçu Uber », « Uber Receipts », « Reçu course Uber ») : jamais « bien reçu »
 * ni « a été reçu », qui accusent réception d'autre chose.
 */
const RECU = /(?<!\b(?:bien|avons|avez|ont|ai|as|a|est|ete|sera|pas|non)\s)\brecus?\b|\breceipts?\b/
/** Le reçu suit l'achat : la pièce qui lui répond est datée du jour du mail, à peu près. */
export const RECU_JOURS = 2
/** Ce qu'un reçu peut donner : la facture de l'achat, ou le reçu lui-même. */
const TYPES_RECU: readonly TypePiece[] = ['facture', 'autre']

/**
 * Le mail est-il le reçu d'un achat ? D'après son objet, son résumé ou le nom de sa boîte
 * d'envoi (« Reçu Uber » : l'objet, « Votre course de mardi », ne le dit pas).
 */
export function recuAnnonce(texte: string, expediteur: string | null): boolean {
  const affiche = (expediteur ?? '').replace(/<[^>]*>/g, ' ')
  return RECU.test(sansAccents(`${texte} ${affiche}`))
}

/** Mois nommé par le texte (« septembre 2026 ») ; une date (« le 15/11/2026 ») n'en est pas un. */
function moisNomme(texte: string): string | undefined {
  return periodeDansTexte(texte.replace(/\b\d{2}\/\d{2}\/\d{4}\b/g, ' ')).periode
}

function moisVoisin(mois: string, pas: 1 | -1): string {
  const [a, m] = mois.split('-').map(Number) as [number, number]
  const i = a * 12 + m - 1 + pas
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`
}

type Candidate = CorrespondanceRecuperation & { ecart: number; jourTache: string }

function evaluer(
  t: RecuperationAttendue,
  p: PieceArrivee,
  privees: ReadonlySet<string> | readonly string[],
): Candidate | null {
  if (t.entiteId !== null && t.entiteId !== p.entiteId) return null
  if (!memeCloison(t.entiteId, p.entiteId, privees)) return null
  const ecart = ecartJours(p.jour, t.jour)
  if (ecart > AVANT_MAIL_JOURS || ecart < -APRES_MAIL_JOURS) return null

  const signaux: SignalRattachement[] = []
  if (p.adresses.some((a) => expediteurCorrespond(a.toLowerCase(), t.expediteur)))
    signaux.push('adresse')
  if (memeEmetteur(t.expediteur, p.noms)) signaux.push('nom')
  if (signaux.length === 0) return null

  const texte = [t.objet, t.resume].filter(Boolean).join(' ')
  const natures = naturesAnnoncees(texte)
  // Un reçu ne nomme ni facture ni montant : il désigne l'achat de son jour, et nul autre.
  const recu = natures.size === 0 && recuAnnonce(texte, t.expediteur)
  if (natures.size > 0) {
    if (!natures.has(p.type)) return null
    signaux.push('nature')
  } else if (recu) {
    if (!TYPES_RECU.includes(p.type) || Math.abs(ecart) > RECU_JOURS) return null
    signaux.push('nature')
  }
  if (p.numero && contientNumero(normaliserLibelle(texte), p.numero)) signaux.push('numero')
  const montant = montantDansTexte(texte)
  if (montant !== undefined && p.montantsCts.length > 0) {
    if (!p.montantsCts.includes(montant)) return null
    signaux.push('montant')
  }
  const mois = moisNomme(texte)
  if (mois) {
    // Facture de septembre émise début octobre, avis d'un prélèvement d'octobre daté de fin
    // septembre : ce mois, le précédent ou le suivant.
    if (![mois, moisVoisin(mois, -1), moisVoisin(mois, 1)].includes(p.jour.slice(0, 7))) return null
    signaux.push('mois')
  }
  // Nature du document tue : l'expéditeur peut n'être qu'une plateforme (banque, logiciel).
  if (!signaux.includes('nature') && !signaux.includes('numero') && !signaux.includes('montant'))
    return null

  const limite = ecart >= 0 ? AVANT_MAIL_JOURS : APRES_MAIL_JOURS
  const score =
    signaux.reduce((s, x) => s + POIDS[x], 0) +
    Math.round(POIDS_DATE * (1 - Math.abs(ecart) / (limite + 1)))
  return { tacheId: t.tacheId, pieceId: p.pieceId, score, signaux, ecart, jourTache: t.jour }
}

/**
 * Les pièces qui répondent aux tâches qui les attendent (`attendPieceAnnoncee`) : chaque couple
 * possible est noté, puis retenu du meilleur au moins bon (plus proche du mail, tâche la plus
 * ancienne), une pièce et une tâche n'étant prises qu'une fois. `privees` : les entités privées
 * (aucune, tout est dans la cloison commune).
 */
export function rattacherRecuperations(
  attendues: readonly RecuperationAttendue[],
  arrivees: readonly PieceArrivee[],
  privees: ReadonlySet<string> | readonly string[] = [],
): CorrespondanceRecuperation[] {
  const candidates: Candidate[] = []
  for (const t of attendues) {
    if (!t.expediteur) continue
    for (const p of arrivees) {
      const c = evaluer(t, p, privees)
      if (c) candidates.push(c)
    }
  }
  candidates.sort(
    (a, b) =>
      b.score - a.score ||
      Math.abs(a.ecart) - Math.abs(b.ecart) ||
      a.jourTache.localeCompare(b.jourTache) ||
      a.tacheId.localeCompare(b.tacheId) ||
      a.pieceId.localeCompare(b.pieceId),
  )
  const taches = new Set<string>()
  const pieces = new Set<string>()
  const retenues: CorrespondanceRecuperation[] = []
  for (const { ecart: _e, jourTache: _j, ...c } of candidates) {
    if (taches.has(c.tacheId) || pieces.has(c.pieceId)) continue
    taches.add(c.tacheId)
    pieces.add(c.pieceId)
    retenues.push(c)
  }
  return retenues
}
